/**
 * PersonNode Component
 *
 * Represents a person card in the family tree visualization.
 * Displays profile image, name, and dates with proper fallbacks.
 * Supports Phase 2 flexible dates with GEDCOM qualifiers.
 */

import { useTranslation } from 'react-i18next';
import { PersonAvatar } from './PersonAvatar';
import { dateService } from '@/services/DateService';
import type { PersonNode as PersonNodeType } from '../types';
import type { DateQualifier } from '@/types/tree-dashboard.types';

interface PersonNodeProps {
  person: PersonNodeType;
  isSelected?: boolean;
  onClick?: () => void;
}

export function PersonNode({ person, isSelected = false, onClick }: PersonNodeProps) {
  const { t } = useTranslation('common');
  const fullName = `${person.first_name} ${person.last_name || ''}`.trim();
  const isDeceased = !!person.death_date;

  /**
   * Format a single date with flexible date support (Phase 2)
   * Priority: exact date > year-only with qualifier > unknown
   */
  const formatSingleDate = (
    exactDate: string | null | undefined,
    year: number | null | undefined,
    qualifier: DateQualifier | null | undefined
  ): { display: string; tooltip: string } => {
    // Priority 1: Exact date
    if (exactDate) {
      const parsedDate = new Date(exactDate);
      if (!isNaN(parsedDate.getTime())) {
        const displayYear = parsedDate.getFullYear();
        // Check for sentinel date (year === 1 means unknown)
        if (displayYear === 1) {
          return { display: t('tree.person.unknown'), tooltip: 'Date unknown' };
        }
        return { display: displayYear.toString(), tooltip: `Exact: ${displayYear}` };
      }
    }

    // Priority 2: Year-only with qualifier (Phase 2 flexible dates)
    if (year) {
      const formattedDate = dateService.formatFlexibleDate(year, qualifier);
      const qualifierLabel = dateService.getQualifierLabel(qualifier);
      return {
        display: formattedDate,
        tooltip: `${qualifierLabel} ${year}`,
      };
    }

    // Priority 3: No date information
    return { display: t('tree.person.unknown'), tooltip: 'Date not available' };
  };

  // Format date range with flexible date support
  const formatDateRange = (
    birthDate?: string | null,
    deathDate?: string | null,
    yob?: number | null,
    yod?: number | null,
    birthQualifier?: DateQualifier | null,
    deathQualifier?: DateQualifier | null
  ): { display: string; tooltip: string } => {
    // No dates at all
    if (!birthDate && !deathDate && !yob && !yod) {
      return {
        display: t('tree.person.datesNotAdded'),
        tooltip: 'Birth and death dates not added',
      };
    }

    const birth = formatSingleDate(birthDate, yob, birthQualifier);
    const death = formatSingleDate(
      deathDate,
      yod,
      deathQualifier
    );

    // Living person (no death info)
    if (!deathDate && !yod) {
      return {
        display: `${birth.display} — ${t('tree.person.present')}`,
        tooltip: `Born: ${birth.tooltip}`,
      };
    }

    // Deceased person
    return {
      display: `${birth.display} — ${death.display}`,
      tooltip: `Born: ${birth.tooltip}\nDied: ${death.tooltip}`,
    };
  };

  const dateInfo = formatDateRange(
    person.birth_date,
    person.death_date,
    (person as any).yob,
    (person as any).yod,
    (person as any).date_of_birth_qualifier,
    (person as any).date_of_death_qualifier
  );

  const dateRange = dateInfo.display;

  return (
    <div
      className={`
        person-card-shadow bg-white dark:bg-slate-900
        border-2 p-3 rounded-xl w-56
        flex items-center gap-4
        cursor-pointer transition-all group
        ring-4 ring-transparent
        ${
          isSelected
            ? 'border-primary shadow-xl shadow-primary/10'
            : 'border-slate-200 dark:border-slate-800 hover:border-primary hover:ring-primary/5'
        }
        ${
          isDeceased
            ? 'opacity-60 saturate-0 border-dashed border-slate-400 dark:border-slate-600'
            : ''
        }
      `}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick?.();
        }
      }}
      aria-label={`Select ${fullName}${isDeceased ? ` (${t('tree.person.deceased')})` : ''}`}
    >
      {/* Selected Badge */}
      {isSelected && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-tighter">
          {t('tree.person.selected')}
        </div>
      )}

      {/* Avatar */}
      <PersonAvatar
        imageUrl={person.profile_image_url}
        gender={person.gender}
        firstName={person.first_name}
        lastName={person.last_name}
        size="sm"
      />

      {/* Info */}
      <div className="flex-1 overflow-hidden">
        <p
          className={`text-sm font-bold truncate transition-colors ${
            isSelected ? 'text-primary' : 'group-hover:text-primary'
          }`}
        >
          {fullName}
        </p>
        <p
          className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-widest font-semibold"
          title={dateInfo.tooltip}
        >
          {dateRange}
        </p>
      </div>
    </div>
  );
}
